<template>
    <NuxtLink
        :to="profileLink"
        class="MentorRow group flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-surface-card"
    >
        <!-- Profile Image -->
        <div class="relative size-14 shrink-0 overflow-hidden rounded-full bg-gray-200 dark:bg-subtle">
            <img
                v-if="mentor.profileImg"
                :src="mentor.profileImg"
                :alt="fullName"
                class="size-full object-cover"
                loading="lazy"
            />
            <div v-else class="flex size-full items-center justify-center bg-gradient-to-br from-[#047274]/20 to-[#05989B]/30 dark:from-[#047274]/30 dark:to-[#05989B]/40">
                <span class="text-xl font-bold text-[#047274]/60 dark:text-[#05989B]/70">{{ nameInitial }}</span>
            </div>
        </div>

        <!-- Main Content -->
        <div class="min-w-0 flex-1">
            <!-- Top row: Name and Location -->
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h3 class="truncate text-base font-semibold text-gray-900 dark:text-white">{{ fullName }}</h3>
                <!-- Same wording and tooltip as the mentor profile (/m/[slug]) -->
                <span
                    v-if="mentor.mentorBooked"
                    class="inline-flex items-center rounded-full bg-orange-100 dark:bg-orange-950/40 px-2 py-0.5 text-xs font-medium text-orange-700 dark:text-orange-300"
                    title="Mentor má aktívny mentoring"
                >
                    Obsadené kapacity
                </span>
                <div v-if="location" class="flex items-center gap-1 text-sm text-gray-500 dark:text-txt-body">
                    <Icon name="heroicons:map-pin" class="size-3.5" />
                    <span class="truncate">{{ location }}</span>
                </div>
            </div>

            <!-- Current Job -->
            <p v-if="currentJob" class="mt-0.5 truncate text-sm text-gray-600 dark:text-txt-body">
                {{ currentJob }}
            </p>

            <!-- Tags - Desktop only -->
            <div v-if="interests && interests.length" class="mt-2 hidden flex-wrap gap-1.5 sm:flex">
                <span
                    v-for="tag in interests.slice(0, tagsMaxCount)"
                    :key="tag"
                    class="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-subtle dark:text-txt-body"
                >
                    {{ tag }}
                </span>
                <span v-if="interests.length > tagsMaxCount" class="px-1 text-xs text-gray-400 dark:text-txt-body">
                    +{{ interests.length - tagsMaxCount }}
                </span>
            </div>
        </div>

        <!-- Action Arrow -->
        <div class="hidden shrink-0 items-center gap-2 sm:flex">
            <span class="text-sm font-medium text-primary-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-400">
                {{ buttonText }}
            </span>
            <Icon name="heroicons:chevron-right" class="size-5 text-gray-400 dark:text-txt-body transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500" />
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import orderBy from 'lodash-es/orderBy'
import type { UserProfile } from '#shared/types/User'
import { getFullName } from '#shared/utils'
import { useUser } from '@/composables/user/useUser'

interface Props {
    mentor: UserProfile
    linkTo?: Record<string, any>
    tagsMaxCount?: number
    buttonText?: string
}

const props = withDefaults(defineProps<Props>(), {
    linkTo: undefined,
    tagsMaxCount: 4,
    buttonText: 'Zobraziť profil'
})

const profileLink = computed(() => {
    if (props.linkTo) return props.linkTo
    const slugOrId = props.mentor.slug || props.mentor.id
    const query = props.mentor.slug && props.mentor.id ? { uid: props.mentor.id } : undefined
    return { name: 'mentor-id', params: { id: slugOrId }, query }
})

const { formatLocation } = useUser()

const fullName = computed(() => getFullName(props.mentor))

const nameInitial = computed(() => props.mentor?.firstName?.charAt(0) || '?')

const currentJob = computed(() => {
    const currentJob = orderBy(props.mentor?.jobs ?? [], 'endYear', 'desc')[0]
    return [currentJob?.position, currentJob?.org].filter(Boolean).join(' @ ')
})

// Typesense hits carry mentorInterests; mentorProfile.interests only exists on Firestore profiles
const interests = computed(
    () => (props.mentor as UserProfile & { mentorInterests?: string[] })?.mentorInterests ?? props.mentor?.mentorProfile?.interests ?? []
)

const location = computed(() => formatLocation(props.mentor))
</script>
